import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Card, Col, Row, Alert, Spinner } from "react-bootstrap";
import { FaServer } from "react-icons/fa";
import { MdEdit, MdDelete } from "react-icons/md";
import Title from "../common/Title";
import Utils from "../../utils";
import { runbookSystemActions } from "../../actions/runbookSystem.actions";
import { useToast } from "../../contexts/ToastContext";
import ConfirmModal from "../common/ConfirmModal";

function RunbookSystemForm({ presetSystem, onSaveSuccess, onCancel, onEditRequest, onDeleted, hideTitle, readOnly }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const runbookSystem = useSelector((state) => state.runbookSystem);
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);

  const queryParams = Utils.getQueryStringParams(location);
  const systemName = presetSystem ?? queryParams["system"];
  const isEditMode = !!systemName;

  const [formData, setFormData] = useState({
    system: "",
    requireSenderId: false,
    requireSenderPassword: false,
    requireClientId: false,
    requireLoginEndpoint: false,
    requireVersion: false,
    requireUserId: false,
    requireCompanyId: false,
    requireClientSecret: false,
    requireUserPassword: false,
    azureUrl: "",
    preAuth: false,
    azureAuthUrl: "",
    apimKey: "",
    iconUrl: "",
    requireRefreshToken: false,
    requireTenantId: false,
    requireScope: false,
    requireDelimiter: false,
    requireHeaderRow: false,
    requireFileMask: false,
    heartbeatUrl: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      dispatch(runbookSystemActions.getById({ system: systemName }));
    }
    // eslint-disable-next-line
  }, [systemName]);

  useEffect(() => {
    if (isEditMode && runbookSystem.system) {
      setFormData({
        system: runbookSystem.system.system || "",
        requireSenderId: runbookSystem.system.requireSenderId || false,
        requireSenderPassword: runbookSystem.system.requireSenderPassword || false,
        requireClientId: runbookSystem.system.requireClientId || false,
        requireLoginEndpoint: runbookSystem.system.requireLoginEndpoint || false,
        requireVersion: runbookSystem.system.requireVersion || false,
        requireUserId: runbookSystem.system.requireUserId || false,
        requireCompanyId: runbookSystem.system.requireCompanyId || false,
        requireClientSecret: runbookSystem.system.requireClientSecret || false,
        requireUserPassword: runbookSystem.system.requireUserPassword || false,
        azureUrl: runbookSystem.system.azureUrl || "",
        preAuth: runbookSystem.system.preAuth || false,
        azureAuthUrl: runbookSystem.system.azureAuthUrl || "",
        apimKey: runbookSystem.system.apimKey || "",
        iconUrl: runbookSystem.system.iconUrl || "",
        requireRefreshToken: runbookSystem.system.requireRefreshToken || false,
        requireTenantId: runbookSystem.system.requireTenantId || false,
        requireScope: runbookSystem.system.requireScope || false,
        requireDelimiter: runbookSystem.system.requireDelimiter || false,
        requireHeaderRow: runbookSystem.system.requireHeaderRow || false,
        requireFileMask: runbookSystem.system.requireFileMask || false,
        heartbeatUrl: runbookSystem.system.heartbeatUrl || "",
      });
    }
    // eslint-disable-next-line
  }, [runbookSystem.system]);

  useEffect(() => {
    if (runbookSystem.success && submitted) {
      showToast("System saved successfully", "success");
      if (onSaveSuccess) {
        onSaveSuccess();
      } else {
        navigate("/product/systems");
      }
    }
    // eslint-disable-next-line
  }, [runbookSystem.success]);

  useEffect(() => {
    if (runbookSystem.deleteSuccess) {
      showToast("System deleted successfully", "success");
      if (onDeleted) {
        onDeleted();
      } else if (onCancel) {
        onCancel();
      } else {
        navigate("/product/systems");
      }
    }
    // eslint-disable-next-line
  }, [runbookSystem.deleteSuccess]);

  const handleDeleteSystem = () => {
    setConfirmDeleteModalShow(false);
    dispatch(runbookSystemActions.remove({ system: systemName }));
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.system.trim()) {
      newErrors.system = "System name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!validate()) {
      return;
    }

    if (isEditMode) {
      dispatch(runbookSystemActions.update(formData));
    } else {
      dispatch(runbookSystemActions.create(formData));
    }
  };

  return (
    <>
      {!hideTitle && (
        <Title
          text={readOnly ? "View Runbook System" : isEditMode ? "Edit Runbook System" : "Add Runbook System"}
          icon={<FaServer className="mb-1" />}
        />
      )}

      {runbookSystem.error && (
        <Alert variant="danger">
          {runbookSystem.errorMessage || "An error occurred while saving the system. Please try again."}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <div className="d-flex gap-2 mb-3">
          {!readOnly && (
            <Button variant="primary" type="submit" disabled={runbookSystem.loading}>
              {runbookSystem.loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>{isEditMode ? "Update" : "Create"} System</>
              )}
            </Button>
          )}
          {readOnly && onEditRequest && (
            <Button
              variant="outline-primary"
              onClick={() => onEditRequest(systemName)}
              disabled={runbookSystem.loading}
            >
              <MdEdit className="mb-1 me-2" />
              Edit
            </Button>
          )}
          {readOnly && (
            <Button
              variant="outline-danger"
              onClick={() => setConfirmDeleteModalShow(true)}
              disabled={runbookSystem.loading}
            >
              <MdDelete className="mb-1 me-2" />
              Delete
            </Button>
          )}
          {!readOnly && (
            <Button
              variant="secondary"
              onClick={() => (onCancel ? onCancel() : navigate("/product/systems"))}
              disabled={runbookSystem.loading}
            >
              Cancel
            </Button>
          )}
        </div>

        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
        <Card className="mb-3">
          <Card.Header>
            <Card.Title>Basic Information</Card.Title>
          </Card.Header>
          <Card.Body>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                System Name <span className="text-danger">*</span>
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={formData.system}
                  onChange={handleChange("system")}
                  disabled={isEditMode}
                  isInvalid={!!errors.system}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.system}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                Azure URL
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={formData.azureUrl}
                  onChange={handleChange("azureUrl")}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                Azure Auth URL
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={formData.azureAuthUrl}
                  onChange={handleChange("azureAuthUrl")}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                APIM Key
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={formData.apimKey}
                  onChange={handleChange("apimKey")}
                  maxLength={36}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                Heartbeat URL
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={formData.heartbeatUrl}
                  onChange={handleChange("heartbeatUrl")}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                Icon URL
              </Form.Label>
              <Col sm={9}>
                <div className="d-flex align-items-start gap-2">
                  <div className="flex-grow-1">
                    <Form.Control
                      type="text"
                      value={formData.iconUrl}
                      onChange={handleChange("iconUrl")}
                      placeholder="https://cdn.example.com/logo.svg"
                    />
                    <Form.Text className="text-muted">Optional. Paste a link to the system's logo.</Form.Text>
                  </div>
                  {formData.iconUrl && (
                    <img
                      src={formData.iconUrl}
                      alt=""
                      style={{ width: 40, height: 40, objectFit: "contain", background: "#fff", border: "1px solid #dee2e6", borderRadius: 6, padding: 4 }}
                      onError={(e) => { e.target.style.visibility = "hidden"; }}
                      onLoad={(e) => { e.target.style.visibility = "visible"; }}
                    />
                  )}
                </div>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Col sm={{ span: 9, offset: 3 }}>
                <Form.Check
                  type="checkbox"
                  label="Pre-Authentication"
                  checked={formData.preAuth}
                  onChange={handleChange("preAuth")}
                />
              </Col>
            </Form.Group>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <Card.Title>Required Authentication Fields</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Require Sender ID"
                  checked={formData.requireSenderId}
                  onChange={handleChange("requireSenderId")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Sender Password"
                  checked={formData.requireSenderPassword}
                  onChange={handleChange("requireSenderPassword")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Client ID"
                  checked={formData.requireClientId}
                  onChange={handleChange("requireClientId")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Client Secret"
                  checked={formData.requireClientSecret}
                  onChange={handleChange("requireClientSecret")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require User ID"
                  checked={formData.requireUserId}
                  onChange={handleChange("requireUserId")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require User Password"
                  checked={formData.requireUserPassword}
                  onChange={handleChange("requireUserPassword")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Company ID"
                  checked={formData.requireCompanyId}
                  onChange={handleChange("requireCompanyId")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Tenant ID"
                  checked={formData.requireTenantId}
                  onChange={handleChange("requireTenantId")}
                  className="mb-2"
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Require Login Endpoint"
                  checked={formData.requireLoginEndpoint}
                  onChange={handleChange("requireLoginEndpoint")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Version"
                  checked={formData.requireVersion}
                  onChange={handleChange("requireVersion")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Refresh Token"
                  checked={formData.requireRefreshToken}
                  onChange={handleChange("requireRefreshToken")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Scope"
                  checked={formData.requireScope}
                  onChange={handleChange("requireScope")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Delimiter"
                  checked={formData.requireDelimiter}
                  onChange={handleChange("requireDelimiter")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require Header Row"
                  checked={formData.requireHeaderRow}
                  onChange={handleChange("requireHeaderRow")}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  label="Require File Mask"
                  checked={formData.requireFileMask}
                  onChange={handleChange("requireFileMask")}
                  className="mb-2"
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
        </fieldset>
      </Form>

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Delete Runbook System"
        question={`Are you sure you want to delete the system "${systemName}"?`}
        noButtonText="Cancel"
        yesButtonText="Yes, Delete"
        handleClose={() => setConfirmDeleteModalShow(false)}
        handleConfirm={handleDeleteSystem}
      />
    </>
  );
}

export default RunbookSystemForm;
